import find from 'lodash/find';
import PropTypes from 'prop-types';
import React from 'react';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import xorBy from 'lodash/xorBy';

import { SHOP_TYPE } from '../../../shops/constants';
import { fullName } from '../../../../common/helpers/users';
import {
  isPaidOnline,
  countTotalCostWithTaxes,
  countTotalCost,
  changedTime,
} from '../../../../common/helpers/appointments';
import ConfirmationScreenSection from './confirmationScreenSection';
import Header from '../header';
import Icon, { GLYPHS } from '../../../../common/icon';
import Money, { formatAmount } from '../../../../common/money';
import SendNotifications from './sendNotifications';
import { RECURRING_FREQUENCIES } from '../../constants';

const formatRecurring = (isRecurring, recurrEveryNumDays) => {
  if (isRecurring) {
    const frequency = find(RECURRING_FREQUENCIES, { value: recurrEveryNumDays });
    const every = recurrEveryNumDays > 7 ? 'every ' : '';
    return `Repeat ${every}${frequency.label}`;
  }

  return 'Non repeatable';
};

const ConfirmationScreen = (props) => {
  const {
    selectedBarber,
    currentBarber,
    selectedServices,
    selectedCustomer,
    selectedDuration,
    day,
    time,
    saveButtonDisabled,
    appointment,
    timezone,
    save,
    saveAll,
    hideConfirmationScreen,
    saveWithoutConfirmation,
    toggleNotifications,
    sendNotifications,
    autoChargeRecurring,
    tipAmount,
    currency,
    isRecurring,
    recurrEveryNumDays,
    onClose,
  } = props;

  // we get shop from currentBarber because appointment does not contain it and shops are same in these sources
  const shop = currentBarber.shop;
  const oldTotal = countTotalCost(appointment.appointment_service) + appointment.tipAmount;
  const newTotal = countTotalCostWithTaxes(selectedServices) + appointment.tipAmount;
  const servicesChanged = xorBy(appointment.appointment_service, selectedServices, 'serviceId').length > 0;
  const showRefundPrompt = isPaidOnline(appointment.payment) && servicesChanged;
  const isBarberChanged = selectedBarber !== appointment.barberId;
  const oldBarberName = appointment.barberFullName;
  const newBarberName = fullName(currentBarber);

  const newAutoChargeRecurring = autoChargeRecurring;
  const oldAutoChargeRecurring = appointment.autoChargeRecurring;
  const isAutoChargeRecurringChanged = newAutoChargeRecurring !== oldAutoChargeRecurring;

  const newTipAmount = tipAmount;
  const oldTipAmount = appointment.tipAmount;
  const isTipAmountChanged = newTipAmount !== oldTipAmount;

  const recurringChanged = appointment.isRecurring !== isRecurring;

  let refundChargeSection;
  if (showRefundPrompt) {
    if (newTotal < oldTotal) {
      refundChargeSection = (
        <div className="layout__item u-pr">
          <div className="c-label c-label--light">Refund</div>
          <p>
            The client will be refunded{ ' ' }
            <span className="u-type--bold u-font-color-primary">
              <Money cents={oldTotal - newTotal} />
            </span>.
          </p>
        </div>
      );
    } else if (newTotal > oldTotal) {
      refundChargeSection = (
        <div className="layout__item u-pr">
          <div className="c-label c-label--light">Charge</div>
          <p>
            The client's card on file will be charged an extra{ ' ' }
            <span className="u-type--bold u-font-color-primary">
              <Money cents={newTotal - oldTotal} />
            </span>.
          </p>
        </div>
      );
    }
  }

  const showPaymentTransferSection = shop.kind === SHOP_TYPE.rental && isBarberChanged && isPaidOnline(appointment.payment);

  const timeChanged = changedTime(day, time, appointment, timezone);
  const isTimeChanged = timeChanged.isTimeChanged;

  const formatService = service => `${service.name} ${formatAmount(service.cost, currency)} / ${service.duration}m`;
  const oldServiceIds = appointment.appointment_service.map(service => service.serviceId);
  const newServiceIds = selectedServices.map(service => service.serviceId || service.id);
  const isServicesChanged = !isEqual(oldServiceIds.sort(), newServiceIds.sort());
  let oldServices;
  let newServices;
  if (isServicesChanged) {
    oldServices = appointment.appointment_service.map(formatService).join(', ');
    newServices = selectedServices.map(formatService).join(', ');
  }

  const oldDuration = appointment.duration.toString();
  const newDuration = selectedDuration.toString();
  const isDurationChanged = newDuration !== oldDuration;

  const oldCustomerName = appointment.customerFullName;
  const newCustomerName = get(selectedCustomer, 'name');
  const isCustomerChanged = newCustomerName !== oldCustomerName;

  if (!refundChargeSection && !showPaymentTransferSection && !isTimeChanged &&
    !isBarberChanged && !isServicesChanged && !isDurationChanged && !isCustomerChanged &&
    !isAutoChargeRecurringChanged && !isTipAmountChanged && !recurringChanged
  ) {
    saveWithoutConfirmation();
    return null;
  }

  // TODO: rewrite it
  const modalWrapper = document.getElementsByClassName('remodal-wrapper');
  if (modalWrapper.length) {
    modalWrapper[0].scrollTop = 0;
  }

  return (
    <div className="c-appointment-confirmation">
      <Header onClose={onClose} />
      <div className="layout__item u-pr">
        <p>Updating the appointment will:</p>
      </div>
      { refundChargeSection }
      <div
        className="layout__item u-pr"
        display-if={showPaymentTransferSection}
      >
        <div className="c-label c-label--light">Payment transfer</div>
        <p>
          The client payment of <span className="u-type--bold u-font-color-primary">
            <Money cents={newTotal} />
          </span>{ ' ' }
          including tip will be transferred{ ' ' }
          from <span className="u-type--bold u-font-color-grey">{ oldBarberName }</span>{ ' ' }
          to <span className="u-type--bold u-font-color-primary">{ newBarberName }</span>.
        </p>
      </div>
      <ConfirmationScreenSection
        display-if={isTimeChanged}
        title="Time"
        oldValue={timeChanged.oldTime}
        newValue={timeChanged.newTime}
      />
      <ConfirmationScreenSection
        display-if={isBarberChanged}
        title="Barber"
        oldValue={oldBarberName}
        newValue={newBarberName}
      />
      <ConfirmationScreenSection
        display-if={isServicesChanged}
        title="Service"
        oldValue={oldServices}
        newValue={newServices}
      />
      <ConfirmationScreenSection
        display-if={isDurationChanged}
        title="Duration"
        oldValue={oldDuration}
        newValue={`${newDuration} mins`}
      />
      <ConfirmationScreenSection
        display-if={isCustomerChanged}
        title="Client"
        oldValue={oldCustomerName}
        newValue={newCustomerName}
      />
      <ConfirmationScreenSection
        display-if={isAutoChargeRecurringChanged}
        title="Auto charge recurring appointments"
        oldValue={oldAutoChargeRecurring ? 'on' : 'off'}
        newValue={newAutoChargeRecurring ? 'on' : 'off'}
      />
      <ConfirmationScreenSection
        display-if={recurringChanged}
        title="Appointment repeat"
        oldValue={formatRecurring(appointment.isRecurring, appointment.recurrEveryNumDays)}
        newValue={formatRecurring(isRecurring, recurrEveryNumDays)}
      />
      <ConfirmationScreenSection
        display-if={isTipAmountChanged}
        title="Preferred tip"
        oldValue={formatAmount(oldTipAmount, currency)}
        newValue={formatAmount(newTipAmount, currency)}
      />
      <SendNotifications
        checked={sendNotifications}
        onChange={toggleNotifications}
      />
      <div className="c-appointment-form__controls u-text-right u-ph- u-pb-">
        <div className="c-separator u-mb- u--mh- u-mt" />
        <a
          className="c-btn c-btn--outline c-btn--outline-grey u-float-left"
          onClick={hideConfirmationScreen}
        >
          <Icon glyph={GLYPHS.refresh} size="medium" />
          <span className="c-btn__text">Back</span>
        </a>
        <button
          className="c-btn"
          type="button"
          onClick={saveAll}
          disabled={saveButtonDisabled}
          display-if={appointment.isRecurring}
        >
          <Icon glyph={GLYPHS.save} size="medium" />
          <span className="c-btn__text">Update All Appointments</span>
        </button>
        <button
          className="c-btn"
          type="button"
          onClick={save}
          disabled={saveButtonDisabled}
        >
          <Icon glyph={GLYPHS.save} size="medium" />
          <span className="c-btn__text">Update Appointment</span>
        </button>
      </div>
    </div>
  );
};

ConfirmationScreen.propTypes = {
  selectedBarber: PropTypes.string.isRequired,
  currentBarber: PropTypes.object.isRequired,
  selectedServices: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedCustomer: PropTypes.object.isRequired,
  selectedDuration: PropTypes.number.isRequired,
  day: PropTypes.string.isRequired,
  time: PropTypes.string.isRequired,
  saveButtonDisabled: PropTypes.bool.isRequired,
  appointment: PropTypes.object.isRequired,
  timezone: PropTypes.string.isRequired,
  save: PropTypes.func.isRequired,
  saveAll: PropTypes.func.isRequired,
  hideConfirmationScreen: PropTypes.func.isRequired,
  saveWithoutConfirmation: PropTypes.func.isRequired,
  toggleNotifications: PropTypes.func.isRequired,
  sendNotifications: PropTypes.bool.isRequired,
  autoChargeRecurring: PropTypes.bool.isRequired,
  tipAmount: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  isRecurring: PropTypes.bool.isRequired,
  recurrEveryNumDays: PropTypes.number,
};

export default ConfirmationScreen;
